import React, { useMemo } from 'react';
import compact from 'lodash/compact';
import { CommonTestAttributes } from 'chaika-test-attributes';

import style from './CheckboxList.module.scss';

import { Checkbox, CheckboxType } from '../Checkbox';
import { FieldTitle, FieldTitleTheme } from '../FieldTitle';
import { translate } from '@chaika/i18n/helper';

export interface CheckboxListProps<I = string> {
    title?: string;
    boldTitle?: boolean;
    options: OptionParams<I>[];
    selectedValues: I[];
    withSelectAll?: boolean;
    type?: CheckboxType;
}

export interface OptionParams<I = string, D = object> {
    label: string;
    value: I;
    data?: D;
    disabled?: boolean;
    dataTest?: string;
    additionalContent?: () => React.ReactNode;
    selected?: boolean;
}

interface Props<I> extends CheckboxListProps<I> {
    onChange(selectedValues: I[]): void;
}

export function CheckboxList<I>({
    title,
    boldTitle,
    options,
    selectedValues,
    withSelectAll,
    type,
    onChange,
}: Props<I>): React.ReactNode {
    const optionsWithoutDisabled = useMemo(() => options.filter(option => !option.disabled), [options]);
    const selectedOptionsWithoutDisabled = useMemo(
        () => makeOptionsFromValues(selectedValues, options).filter(option => !option.disabled),
        [selectedValues, options],
    );

    const isAllChecked = optionsWithoutDisabled.length === selectedOptionsWithoutDisabled.length;

    const handleChange = ({ target: { value, checked } }: React.ChangeEvent<HTMLInputElement>): void => {
        const newSelectedValues = checked
            ? [...selectedValues, value]
            : selectedValues.filter(selectedValue => String(selectedValue) !== value);

        onChange(newSelectedValues as I[]);
    };

    const handleChangeAll = (): void => {
        if (selectedOptionsWithoutDisabled.length !== optionsWithoutDisabled.length) {
            onChange(optionsWithoutDisabled.map(option => option.value));
        } else {
            const disabledSelectedValues = makeOptionsFromValues(selectedValues, options)
                .filter(option => option.disabled)
                .map(option => option.value);

            onChange(disabledSelectedValues);
        }
    };

    return (
        <div className={style.root}>
            {title && (
                <FieldTitle
                    title={title}
                    theme={boldTitle ? FieldTitleTheme.Bold : undefined}
                />
            )}
            {withSelectAll && (
                <div className={style.checkboxWrapper}>
                    <Checkbox
                        type={type}
                        onChange={handleChangeAll}
                        isChecked={isAllChecked}
                        disabled={options?.length === 0 || options.every(option => option.disabled)}
                        dataTest={CommonTestAttributes.Checkbox}
                        label={translate('selectAll')}
                    />
                </div>
            )}
            {options.map(option => (
                <div
                    className={style.checkboxWrapper}
                    key={String(option.value)}
                >
                    <Checkbox
                        type={type}
                        isChecked={selectedValues.includes(option.value)}
                        onChange={handleChange}
                        disabled={option.disabled}
                        dataTest={option.dataTest ?? `${CommonTestAttributes.Checkbox}_${String(option.value)}`}
                        label={option.label}
                        value={String(option.value)}
                    />
                    {option.additionalContent && <div className={style.additional}>{option.additionalContent()}</div>}
                </div>
            ))}
        </div>
    );
}

function makeOptionsFromValues<I>(values: I[], options: OptionParams<I>[]): OptionParams<I>[] {
    return compact(values.map(value => options.find(option => option.value === value)));
}
